function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

// TS's lib.dom ScrollBehavior type predates the "instant" keyword, though it's
// valid per spec and supported by all current browsers.
const INSTANT = "instant" as ScrollBehavior;

// Lets a newer scroll call supersede an in-flight one (e.g. clicking a second
// nav link before the first animation finishes) instead of both fighting
// over window.scrollTo() every frame.
let activeScrollToken = 0;

/**
 * Eased scroll animation. Native `scrollIntoView({behavior:"smooth"})` uses the
 * browser's own (often linear/abrupt) curve — this gives full control over the
 * easing so section navigation feels intentional rather than instant.
 */
export function smoothScrollTo(target: HTMLElement | number, duration = 900): void {
  const startY = window.scrollY;
  const targetY = typeof target === "number" ? target : startY + target.getBoundingClientRect().top;
  const distance = targetY - startY;

  if (Math.abs(distance) < 1) return;

  const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (prefersReduced) {
    window.scrollTo({ top: targetY, behavior: INSTANT });
    return;
  }

  const token = ++activeScrollToken;
  let startTime: number | null = null;

  function step(timestamp: number) {
    if (token !== activeScrollToken) return; // superseded by a newer scroll call
    if (startTime === null) startTime = timestamp;
    const elapsed = timestamp - startTime;
    const progress = Math.min(elapsed / duration, 1);
    window.scrollTo({ top: startY + distance * easeInOutCubic(progress), left: 0, behavior: INSTANT });
    if (progress < 1) {
      requestAnimationFrame(step);
    }
  }

  requestAnimationFrame(step);
}

const NAV_OFFSET = 84;

export function smoothScrollToId(id: string, duration = 900, offset = NAV_OFFSET): void {
  const el = document.getElementById(id);
  if (!el) return;
  const targetY = window.scrollY + el.getBoundingClientRect().top - offset;
  smoothScrollTo(Math.max(targetY, 0), duration);
}

export function smoothScrollToTop(duration = 900): void {
  smoothScrollTo(0, duration);
}
